import React, {ReactNode, useCallback, useState} from 'react';

import debounce from 'lodash/debounce';

import {
  ActionMeta,
  CreatableProps as SelectProps,
  SweetCreatableReactSelect,
  SweetReactSelect,
} from 'app/components/general/SweetReactSelect';
import {InfiniteListRTK} from 'app/general/datafetchers/InfiniteListRTK';
import {SELECT_PAGE_SIZE} from 'app/utils/constants';

import {SelectMenuList} from './SelectMenu';
import {SelectOption} from './SelectOption';
import {SelectMultiValueLabel, SelectSingleValue} from './SelectValue';

import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {ValueType} from 'react-select';

interface CommonProps<
  IT extends {id: number | string},
  FP extends {page: string},
  P,
  V = IT
> {
  selected: Array<IT> | IT | null;
  placeholder: string;
  selectLimit?: number;
  selectLimitText?: ReactNode;
  isClearable?: boolean;
  menuIsOpen?: boolean;
  onInputChange?: (value: string) => any;
  selectProps?: Partial<SelectProps<IT, V, ReactNode>>;
  components?: {[component: string]: any};
  children?: ReactNode;
  disabled?: boolean;
  refetchDataOnOpen?: boolean;
  isRTKFetcher?: boolean;
  params?: P;
  searchOrderingParam?: {ordering: '-rank' | 'name'};
  typeIcon?: null | string | ((value: IT) => string | null);
  translateSearchParams?: (
    param: string,
    opts?: {ordering?: string}
  ) => {[param: string]: string | number};
  orderFunction?: (items: Array<IT>) => Array<IT>;
  addFirstOption?: IT;
  creatable?: boolean;
  noOptionsMessage?: (inputValue: string) => React.ReactNode;
  formatOptionLabel?: (option: IT) => string | React.ReactNode;
  getOptionLabel?: (option: IT) => string;
  useDataHook: UseListQueryType<FP, IT>;
  paramsConverter: (query: Record<string, any>) => FP;
}

interface SingleItemProps<
  IT extends {id: number | string},
  FP extends {page: string},
  P,
  V
> extends CommonProps<IT, FP, P, V> {
  isMulti: false;
  selected: IT | null;
  onChange: (value: IT | null, meta: ActionMeta<IT>) => Promise<unknown>;
}

interface MultiItemProps<
  IT extends {id: number | string},
  FP extends {page: string},
  P,
  V
> extends CommonProps<IT, FP, P, V> {
  isMulti: true;
  selected: Array<IT>;
  onChange: (values: Array<IT>, meta: ActionMeta<IT>) => Promise<unknown>;
}

export type SelectBaseRTKProps<
  T extends {id: number | string},
  FP extends {page: string},
  P extends Record<string, any> = Record<string, any>,
  V = T
> = SingleItemProps<T, FP, P, V> | MultiItemProps<T, FP, P, V>;

export function SelectBaseRTK<
  IT extends {id: number | string},
  FP extends {page: string},
  P extends Record<string, any> = Record<string, any>,
  V = IT
>({
  selectLimitText,
  selectLimit,
  children,
  selectProps,
  isMulti,
  isRTKFetcher,
  isClearable = true,
  placeholder,
  disabled,
  typeIcon,
  addFirstOption,
  selected,
  onChange,
  onInputChange,
  refetchDataOnOpen,
  orderFunction,
  formatOptionLabel,
  getOptionLabel,
  noOptionsMessage,
  menuIsOpen,
  components = {},
  params = {} as P,
  translateSearchParams = (search: string, params?: {ordering?: string}) => ({
    ordering: search === '' ? 'name' : '-rank',
    ...params,
    search,
  }),
  searchOrderingParam = {ordering: '-rank'},
  creatable = false,
  useDataHook,
  paramsConverter,
}: SelectBaseRTKProps<IT, FP, P, V>) {
  const [loading, setLoading] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [inputText, setInputText] = useState('');
  const [search, setSearch] = useState('');
  const [fetchOnOpen, setFetchOnOpen] = useState(refetchDataOnOpen === true);

  const delayedSearch = useCallback(
    debounce((search: string) => {
      setWaiting(false);
      setSearch(search);
    }, 500),
    []
  );

  const onLimit =
    selectLimit != null &&
    selected != null &&
    Array.isArray(selected) &&
    selected.length >= selectLimit;
  let orderedParams = {...params};
  // Captured before the `if (search...)` reassignment below to preserve the
  // original fetch params (that reassignment ran after the fetch, so it never
  // affected what was loaded).
  const listFetchParams = {
    ...orderedParams,
    ...translateSearchParams(search, orderedParams),
    page_size: SELECT_PAGE_SIZE,
  };

  const clearLoading = (value: IT) => {
    setLoading(false);
    return value;
  };

  const handleOnChange = (value: ValueType<IT>, action: ActionMeta<IT>) => {
    setLoading(true);
    return Promise.resolve(onChange(value || (isMulti ? [] : null), action))
      .then(clearLoading)
      .catch(clearLoading);
  };

  const handleCreateOption = (value: string) => {
    setLoading(true);
    return Promise.resolve(
      selectProps != null && selectProps.onCreateOption != null
        ? selectProps.onCreateOption(value)
        : null
    )
      .then(clearLoading)
      .catch(clearLoading);
  };

  const handleInputChange = (newText: string) => {
    if (newText !== inputText) {
      setInputText(newText);
      setWaiting(true);
      if (onInputChange != null) {
        onInputChange(newText);
      }
      delayedSearch(newText);
    }
    return newText;
  };

  const maybeFetch = (fetchFunc: () => unknown) => {
    if (fetchOnOpen) {
      fetchFunc();
      setFetchOnOpen(false);
    }
  };

  const wrapIfRequired = (
    content: () => JSX.Element,
    children: ReactNode | void,
    onLimit: boolean,
    selectLimitText: ReactNode | void
  ): JSX.Element => {
    if (children != null || onLimit) {
      return (
        <div>
          {content()}
          {children}
          {onLimit ? (
            <div className="small form-group mt-2">
              {selectLimitText ?? null}
            </div>
          ) : null}
        </div>
      );
    }
    return content();
  };

  const mergedSelectProps = {
    getNewOptionData: (title: string) => title,
    ...selectProps,
    onCreateOption: handleCreateOption,
  };

  const SelectComponent = creatable
    ? SweetCreatableReactSelect
    : SweetReactSelect;

  if (search !== '' && params != null && params.ordering != null) {
    orderedParams = {...params, ...searchOrderingParam};
  }

  return (
    <InfiniteListRTK
      fetchParams={listFetchParams}
      useDataHook={useDataHook}
      paramsConverter={paramsConverter}
    >
      {({
        hasNext,
        isLoading,
        loadNext,
        results: rawResults,
        isFetching,
        refetch,
      }) =>
        wrapIfRequired(
          () => {
            const results = orderFunction
              ? orderFunction(rawResults)
              : rawResults;

            const options: Array<IT> = onLimit
              ? []
              : addFirstOption != null && results.length
              ? [addFirstOption as IT, ...results]
              : results;

            const loadingState =
              waiting ||
              loading ||
              (isFetching && !fetchOnOpen) ||
              (isLoading && isRTKFetcher); // isMulti uses new infinite fetcher that behaves differently.

            return (
              <SelectComponent<IT, V>
                onMenuOpen={() => maybeFetch(refetch)}
                isClearable={isClearable}
                isDisabled={disabled}
                isMulti={isMulti}
                isLoading={loadingState}
                getOptionValue={(obj) => (obj as any).url}
                getOptionLabel={getOptionLabel || ((obj) => (obj as any).name!)}
                formatOptionLabel={
                  formatOptionLabel || ((obj) => (obj as any).name!)
                }
                components={{
                  MenuList: SelectMenuList,
                  Option: SelectOption,
                  MultiValueLabel: SelectMultiValueLabel,
                  SingleValue: SelectSingleValue,
                  ...components,
                }}
                filterOption={() => true}
                placeholder={placeholder}
                value={selected}
                onChange={handleOnChange}
                onInputChange={handleInputChange}
                options={options}
                menuIsOpen={waiting && !options.length ? false : menuIsOpen}
                noOptionsMessage={noOptionsMessage}
                {...mergedSelectProps}
                // extra props passed on to compnents in selectProps
                extraProps={{
                  inputText,
                  hasNext,
                  loadNext,
                  waiting,
                  loadingLast: isLoading,
                  onLimit,
                  selectLimitText,
                  typeIcon,
                  ...(selectProps?.extraProps ?? {}),
                }}
              />
            );
          },
          children,
          onLimit,
          selectLimitText
        )
      }
    </InfiniteListRTK>
  );
}
